/**
 * UnreadCount — the ONE rendering of an unread number. (JW-06 / JW-07)
 *
 * The Inbox tab drew it as a solid primary pill, the bell's Unread tab as a
 * faint orange pill and the bell icon as a warning badge — three looks for one
 * number, one screen apart. Every place now draws this: the brand pill, capped
 * at `max` ("99+"), nothing at all at zero.
 *
 * `overlay` pins it to the corner of an icon button (the bell), where the room
 * is one digit, so callers pass `max={9}` there. A caller whose control already
 * SAYS the count (the bell's accessible name, a live region) passes
 * `aria-hidden` so it is not read twice.
 */
import * as React from 'react';

import { cn } from '../lib/utils';

export interface UnreadCountProps extends React.HTMLAttributes<HTMLSpanElement> {
  count: number;
  /** Above this, the pill reads `${max}+`. */
  max?: number;
  /** Pinned to the top-right corner of a `relative` icon button. */
  overlay?: boolean;
}

export function UnreadCount({
  count,
  max = 99,
  overlay = false,
  className,
  ...rest
}: UnreadCountProps) {
  if (count <= 0) return null;
  return (
    <span
      data-slot="unread-count"
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-2xs font-semibold leading-none tabular-nums text-primary-foreground',
        overlay && 'absolute -right-1 -top-1 border-2 border-background',
        className
      )}
      {...rest}
    >
      {count > max ? `${max}+` : count}
    </span>
  );
}
